import { useEffect } from 'react'
import { useAppDispatch, type AppDispatch } from 'common/hooks/redux'
import { procedureApi } from 'features/Procedure/state/procedure.api'
import type { PatientEncounter } from 'features/Procedure/state/procedure-api.type'

type Params = {
  selectedEncounterUid: string | null
  encounters: PatientEncounter[]
  siteId: string | undefined
  selectedPatientId: number | null
}

const PREFETCH_IDLE_TIMEOUT_MS = 2000

export const prefetchPreviousEncounter = (
  dispatch: AppDispatch,
  { selectedEncounterUid, encounters, siteId, selectedPatientId }: Params,
): void => {
  if (!selectedEncounterUid || !siteId || !selectedPatientId) return

  const selectedIndex = encounters.findIndex((e) => e.encounter_uuid === selectedEncounterUid)
  if (selectedIndex < 0) return

  const previous = encounters[selectedIndex + 1]
  if (!previous) return

  const previousUid = previous.encounter_uuid

  dispatch(
    procedureApi.util.prefetch(
      'fetchProcedures',
      { siteId, encounterUid: previousUid },
      { force: false },
    ),
  )
  dispatch(
    procedureApi.util.prefetch(
      'fetchProducts',
      { siteId, encounterUid: previousUid },
      { force: false },
    ),
  )
  dispatch(
    procedureApi.util.prefetch(
      'fetchEncounterPhotos',
      { siteId, patientId: selectedPatientId, encounterUid: previousUid },
      { force: false },
    ),
  )
  dispatch(
    procedureApi.util.prefetch(
      'fetchSoapNotes',
      { patientPid: selectedPatientId, encounterUid: previousUid },
      { force: false },
    ),
  )
}

export const usePrefetchPreviousEncounter = (params: Params): void => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!params.selectedEncounterUid || !params.siteId || !params.selectedPatientId) return

    const run = () => prefetchPreviousEncounter(dispatch, params)

    const ric = window.requestIdleCallback
    if (typeof ric === 'function') {
      const handle = ric(run, { timeout: PREFETCH_IDLE_TIMEOUT_MS })
      return () => {
        window.cancelIdleCallback?.(handle)
      }
    }

    const handle = window.setTimeout(run, 0)
    return () => {
      window.clearTimeout(handle)
    }
  }, [
    dispatch,
    params.selectedEncounterUid,
    params.encounters,
    params.siteId,
    params.selectedPatientId,
  ])
}
